<script setup lang="ts">
import { computed, ref } from 'vue'
import { Link } from '@inertiajs/vue3'
import Button from '@/Components/Utils/Button.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'
import Select from 'primevue/select'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import { FilterMatchMode } from '@primevue/core/api'

type PaymentMethodOption = {
    label: string
    value: string
}

type EntryTypeOption = {
    label: string
    value: string
}

type Transaction = {
    id: string
    model_id: number | null
    source: 'invoice_payment' | 'manual'
    source_label: string
    entry_type: 'income' | 'expense'
    entry_type_label: string
    description: string
    amount: number
    payment_method: string
    payment_method_label: string
    transaction_date: string
    transaction_at: string
    created_by: string | null
    updated_by: string | null
    can_edit: boolean
    can_delete: boolean
    invoice: null | {
        reference: string
        slug: string
        created_at: string
        orders_count: number
        customer_name: string | null
    }
}

type TransactionRow = Transaction & {
    invoice_reference: string
    invoice_customer_name: string
    invoice_orders_count: number | null
}

const props = defineProps<{
    transactions: Transaction[]
    paymentMethodOptions: PaymentMethodOption[]
    entryTypeOptions: EntryTypeOption[]
}>()

const emit = defineEmits<{
    (e: 'edit', transaction: TransactionRow): void
    (e: 'delete', transaction: TransactionRow): void
}>()

const transactionRows = computed<TransactionRow[]>(() => (
    props.transactions.map((transaction) => ({
        ...transaction,
        invoice_reference: transaction.invoice?.reference ?? '',
        invoice_customer_name: transaction.invoice?.customer_name ?? '',
        invoice_orders_count: transaction.invoice?.orders_count ?? null,
    }))
))

const transactionFilters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    entry_type: { value: null, matchMode: FilterMatchMode.EQUALS },
    payment_method: { value: null, matchMode: FilterMatchMode.EQUALS },
})

const clearTableFilters = () => {
    transactionFilters.value.global.value = null
    transactionFilters.value.entry_type.value = null
    transactionFilters.value.payment_method.value = null
}
</script>

<template>
    <section class="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
        <div class="flex flex-col gap-4">
            <div>
                <h3 class="text-base font-semibold">Daftar Transaksi</h3>
                <p class="mt-1 text-sm text-gray-500">
                    Pembayaran invoice hanya tampil sebagai referensi. Jika perlu mengubahnya, lakukan dari halaman invoice.
                </p>
            </div>

            <div class="flex gap-3">
                <InputText
                    v-model="transactionFilters.global.value"
                    placeholder="Cari deskripsi, invoice, customer, pembuat..."
                />
                <div class="flex xgrid grid-cols-1 gap-3 md:grid-cols-2">
                    <Select
                        v-model="transactionFilters.entry_type.value"
                        :options="entryTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        showClear
                        placeholder="Jenis"
                        inputClass="!w-32"
                    />
                    <Select
                        v-model="transactionFilters.payment_method.value"
                        :options="paymentMethodOptions"
                        optionLabel="label"
                        optionValue="value"
                        showClear
                        placeholder="Metode"
                        inputClass="!w-40"
                    />
                </div>
                <div class="flex justify-end">
                    <Button type="gray" size="xs" label="Reset Filter" @click="clearTableFilters" />
                </div>
            </div>
        </div>

        <div class="mt-5">
            <DataTable
                :value="transactionRows"
                v-model:filters="transactionFilters"
                dataKey="id"
                paginator
                :rows="10"
                :rowsPerPageOptions="[10, 25, 50]"
                filterDisplay="menu"
                removableSort
                stripedRows
                showGridlines
                size="small"
                sortField="transaction_at"
                :sortOrder="-1"
                :globalFilterFields="[
                    'description',
                    'entry_type_label',
                    'payment_method_label',
                    'invoice_reference',
                    'invoice_customer_name',
                    'created_by',
                    'updated_by'
                ]"
                tableStyle="min-width: 72rem"
            >
                <template #empty>
                    <div class="py-8 text-center text-sm text-gray-500">
                        Belum ada transaksi pada rentang tanggal ini.
                    </div>
                </template>

                <Column field="transaction_at" header="Tanggal" sortable style="min-width: 12rem">
                    <template #body="{ data }">
                        <div class="text-gray-600">
                            <div class="text-sm font-medium text-gray-800">
                                {{ useFormatTime(data.transaction_at, { formatTime: 'dd MMM yyyy, HH:mm' }) }}
                            </div>
                            <div class="text-xs text-gray-500">
                                {{ useFormatTime(data.transaction_date, { formatTime: 'EEEE' }) }}
                            </div>
                        </div>
                    </template>
                </Column>

                <Column field="entry_type" header="Jenis" sortable style="min-width: 9rem">
                    <template #body="{ data }">
                        <span
                            class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                            :class="data.entry_type === 'income'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-rose-100 text-rose-700'"
                        >
                            {{ data.entry_type_label }}
                        </span>
                    </template>
                </Column>

                <Column field="description" header="Deskripsi" sortable style="min-width: 24rem">
                    <template #body="{ data }">
                        <div>
                            <div class="text-sm font-medium">
                                {{ data.description }}
                            </div>

                            <template v-if="data.invoice">
                                <div class="mt-1 text-xs text-gray-500">
                                    Invoice:
                                    <Link :href="route('casheer.invoices.show', data.invoice.slug)" class="font-medium text-sky-700 hover:underline">
                                        {{ data.invoice.reference }}
                                    </Link>
                                    - {{ data.invoice_customer_name || '-' }}
                                    - {{ data.invoice_orders_count }} order
                                </div>
                                <div class="text-xs text-gray-500">
                                    Dibuat: {{ useFormatTime(data.invoice.created_at, { formatTime: 'dd MMM yyyy' }) }}
                                </div>
                            </template>

                            <template v-else>
                                <div class="mt-1 text-xs text-gray-500">
                                    Input oleh {{ data.created_by || '-' }}
                                    <span v-if="data.updated_by && data.updated_by !== data.created_by">
                                        - terakhir diubah {{ data.updated_by }}
                                    </span>
                                </div>
                            </template>
                        </div>
                    </template>
                </Column>

                <Column field="payment_method" header="Metode" sortable style="min-width: 10rem">
                    <template #body="{ data }">
                        <span class="text-sm">{{ data.payment_method_label }}</span>
                    </template>
                </Column>

                <Column field="amount" header="Jumlah" sortable style="min-width: 10rem">
                    <template #body="{ data }">
                        <div
                            class="text-sm text-right font-semibold tabular-nums"
                            :class="data.entry_type === 'income' ? 'text-emerald-700' : 'text-rose-700'"
                        >
                            {{ data.entry_type === 'income' ? '+' : '-' }}{{ useFormatCurrency(data.amount) }}
                        </div>
                    </template>
                </Column>

                <Column header="Aksi" style="min-width: 10rem">
                    <template #body="{ data }">
                        <div class="flex justify-end gap-2">
                            <Button
                                v-if="data.can_edit"
                                type="tertiary"
                                size="xs"
                                icon="fal fa-pencil"
                                label="Ubah"
                                @click="emit('edit', data)"
                            />
                            <Button
                                v-if="data.can_delete"
                                type="delete"
                                size="xs"
                                label="Hapus"
                                @click="emit('delete', data)"
                            />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </section>
</template>
